import React, { useState, useEffect } from 'react';
import { Moon, Sun, Download, User, MapPin, Mail, FileText, AlertCircle, RefreshCw, LogOut, Eye, ArrowLeft, Menu, Search, Filter, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../Api/apiconnector';

const ECGReportsPage = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    has_previous: false,
    has_next: false
  });
  const [availableFilters, setAvailableFilters] = useState({
    testDates: [],
    reportDates: [],
    locations: []
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTestDate, setSelectedTestDate] = useState('');
  const [selectedReportDate, setSelectedReportDate] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  // Initialize dark mode from localStorage or system preference
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedMode !== null) {
      setDarkMode(JSON.parse(savedMode));
    } else {
      setDarkMode(systemPrefersDark);
    }
  }, []);

  // Apply dark mode class to document root
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Load user data from localStorage
  useEffect(() => {
    const loadUserData = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const user = JSON.parse(storedUser);
          console.log("User data from localStorage:", user);
          setUserData(user);
        } else {
          setError("No user data found in localStorage");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading user data:", err);
        setError("Failed to load user data");
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  // Fetch ECG reports from API
  useEffect(() => {
    if (userData) {
      fetchReports();
    } else if (userData === null) {
      // This case is already handled in loadUserData
    }
  }, [userData]);

  // Auto-apply filters when they change
  useEffect(() => {
    if (userData) {
      // Add a small debounce to prevent too many API calls
      const timeoutId = setTimeout(() => {
        fetchReports({ page: 1 });
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, selectedTestDate, selectedReportDate, selectedLocation]);

  const fetchReports = async (filters = {}) => {
    setLoading(true);
    setError('');

    try {
      // Use passed filters or current state
      const currentSearch = filters.search !== undefined ? filters.search : searchTerm;
      const currentTestDate = filters.test_date !== undefined ? filters.test_date : selectedTestDate;
      const currentReportDate = filters.report_date !== undefined ? filters.report_date : selectedReportDate;
      const currentLocation = filters.location !== undefined ? filters.location : selectedLocation;
      const currentPage = filters.page || 1;

      const params = new URLSearchParams();

      // Only add non-empty parameters
      if (currentSearch.trim()) params.append('search', currentSearch.trim());
      if (currentTestDate) params.append('test_date', currentTestDate);
      if (currentReportDate) params.append('report_date', currentReportDate);
      if (currentLocation) params.append('location', currentLocation);
      if (currentPage > 1) params.append('page', currentPage.toString());

      console.log('Fetching reports with params:', params.toString());

      const response = await fetch(`${API_BASE_URL}/get-ecg-reports/?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log("API Response status:", response.status);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("API Response data:", data);
      
      if (data.success) {
        setReportsData(data);
        
        // Handle pagination data - be more flexible with API response structure
        setPagination({
          current_page: data.pagination?.current_page || data.current_page || 1,
          total_pages: data.pagination?.total_pages || data.total_pages || 1,
          total_items: data.pagination?.total_items || data.total_items || (data.reports ? data.reports.length : 0),
          has_previous: data.pagination?.has_previous || data.has_previous || false,
          has_next: data.pagination?.has_next || data.has_next || false
        });

        // Handle filter options - be more flexible with API response structure
        setAvailableFilters({
          testDates: data.test_dates || data.Test_Date || data.available_filters?.test_dates || [],
          reportDates: data.report_dates || data.Report_Date || data.available_filters?.report_dates || [],
          locations: data.locations || data.Location || data.available_filters?.locations || [],
        });
        
        setSelectedReports(new Set());
      } else {
        throw new Error(data.error || data.message || 'Failed to fetch ECG reports');
      }
    } catch (error) {
      console.error('Error fetching ECG reports:', error);
      setError(error.message || 'Unable to fetch ECG reports. Please try again later.');
      
      if (error.message.includes('Authentication failed') || error.message.includes('401') || error.message.includes('403')) {
        setTimeout(() => {
          localStorage.removeItem("user");
          window.location.href = "/";
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchReports({ page });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTestDate('');
    setSelectedReportDate('');
    setSelectedLocation('');
    // No need to call fetchReports here as the useEffect will trigger it automatically
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.href = "/";
  };

  const handleBackToDashboard = () => {
    window.location.href = '/ecg-patient-dashboard';
  };

  const downloadPDF = (pdfUrl, reportName) => {
    if (!pdfUrl) {
      setError('PDF URL not available for this report');
      return;
    }

    try {
      const fullPdfUrl = pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}${pdfUrl}`;
      const link = document.createElement('a');
      link.href = fullPdfUrl;
      link.download = `${reportName || 'ECG_Report'}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF. Please try again.');
    }
  };

  const viewPDF = (pdfUrl) => {
    if (!pdfUrl) {
      setError('PDF URL not available for this report');
      return;
    }
    const fullPdfUrl = pdfUrl.startsWith('http') ? pdfUrl : `${API_BASE_URL}${pdfUrl}`;
    window.open(fullPdfUrl, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const refreshData = async () => {
    if (!userData) {
      setError('User authentication required');
      return;
    }
    fetchReports();
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (!reportsData?.reports) return;
    
    if (selectedReports.size === reportsData.reports.length) {
      setSelectedReports(new Set());
    } else {
      const allIds = reportsData.reports.map(report => report.id);
      setSelectedReports(new Set(allIds));
    }
  };

  const toggleSelectReport = (reportId) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  // Bulk download functionality
  const handleBulkDownload = async () => {
    if (selectedReports.size === 0) {
      setError('Please select at least one report to download');
      return;
    }

    setDownloadingBulk(true);
    
    try {
      const selectedReportIds = Array.from(selectedReports);
      const selectedReportObjects = reportsData.reports.filter(report => 
        selectedReportIds.includes(report.id)
      );

      for (let i = 0; i < selectedReportObjects.length; i++) {
        const report = selectedReportObjects[i];
        console.log(`Downloading report ${i + 1}/${selectedReportObjects.length}:`, report.name);
        
        downloadPDF(report.pdf_url, report.name || `ECG_Report_${report.patient_id}`);
        
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setError('');
      setSelectedReports(new Set());
    } catch (error) {
      console.error('Error in bulk download:', error);
      setError('Some downloads failed. Please try again.');
    } finally {
      setDownloadingBulk(false);
    }
  };

  const isAllSelected = reportsData?.reports && reportsData.reports.length > 0 && selectedReports.size === reportsData.reports.length;
  const isIndeterminate = selectedReports.size > 0 && (!reportsData?.reports || selectedReports.size < reportsData.reports.length);

  // Show loading only when actually loading and we have user data but no reports data yet
  if (loading && (!reportsData || !reportsData.reports)) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading ECG Reports...</p>
        </div>
      </div>
    );
  }

  // Show error only if there's an error and no reports data
  if (error && (!reportsData || !reportsData.reports || reportsData.reports.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error Loading Reports</h2>
            <p className="mb-4 text-gray-600 dark:text-gray-400">{error}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={refreshData}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 justify-center transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={handleBackToDashboard}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 justify-center transition-colors"
              >
                Back to Dashboard
              </button>
              <button
                onClick={handleLogout}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 justify-center transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Login Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button 
                className="md:hidden p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <Menu className="w-5 h-5 text-gray-900 dark:text-white" />
              </button>
              <button
                onClick={handleBackToDashboard}
                className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 shadow-sm"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </button>
              <div>
                <h1 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white flex items-center transition-colors duration-200">
                  <FileText className="mr-3 h-6 w-6 text-red-600 dark:text-red-400" />
                  ECG Reports
                </h1>
                <p className="text-red-600 dark:text-red-400 mt-1 text-sm transition-colors duration-200">
                  Total Reports: {pagination.total_items || reportsData?.reports?.length || 0}
                </p>
              </div>
            </div>
            
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center space-x-4">
              {selectedReports.size > 0 && (
                <button
                  onClick={handleBulkDownload}
                  disabled={downloadingBulk}
                  className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200 shadow-sm"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloadingBulk ? 'Downloading...' : `Download Selected (${selectedReports.size})`}
                </button>
              )}
              
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 bg-white dark:bg-gray-700"
                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 text-yellow-400" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 mt-4 transition-colors duration-200">
              {selectedReports.size > 0 && (
                <button
                  onClick={handleBulkDownload}
                  disabled={downloadingBulk}
                  className="flex items-center w-full px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200 mb-3 justify-center"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloadingBulk ? 'Downloading...' : `Download Selected (${selectedReports.size})`}
                </button>
              )}
              
              <button
                onClick={toggleDarkMode}
                className="flex items-center w-full px-3 py-2 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                {darkMode ? (
                  <Sun className="w-5 h-5 mr-3 text-yellow-400" />
                ) : (
                  <Moon className="w-5 h-5 mr-3 text-gray-600" />
                )}
                <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message - Show only if there's an error but we still have data */}
        {error && reportsData?.reports && reportsData.reports.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800 dark:text-red-300 font-medium">Error</p>
              <p className="text-red-700 dark:text-red-400 text-sm mt-1">{error}</p>
              <button
                onClick={() => setError('')}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6 mb-6 transition-colors duration-300">
          <div className="space-y-4">
            {/* Desktop Search */}
            <div className="hidden md:flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search by patient name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 border rounded-lg flex items-center transition-colors duration-200 ${
                  showFilters 
                    ? 'bg-red-600 border-red-700 text-white' 
                    : 'border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-600 text-gray-900 dark:text-white bg-white dark:bg-gray-700'
                }`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </button>
            </div>

            {/* Mobile Search */}
            <div className="md:hidden">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by patient name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
              <div className="flex space-x-2 mt-3">
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex-1 py-2 border rounded-lg flex items-center justify-center transition-colors duration-200 ${
                    showFilters 
                      ? 'bg-red-600 border-red-700 text-white' 
                      : 'border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-600 text-gray-900 dark:text-white bg-white dark:bg-gray-700'
                  }`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </button>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors duration-300">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <Calendar className="w-4 h-4 inline mr-1 text-red-600 dark:text-red-400" />
                      Test Date
                    </label>
                    <select
                      value={selectedTestDate}
                      onChange={(e) => setSelectedTestDate(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Test Dates</option>
                      {availableFilters.testDates?.map((date) => (
                        <option key={date} value={date}>{formatDate(date)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <FileText className="w-4 h-4 inline mr-1 text-red-600 dark:text-red-400" />
                      Report Date
                    </label>
                    <select
                      value={selectedReportDate}
                      onChange={(e) => setSelectedReportDate(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Report Dates</option>
                      {availableFilters.reportDates?.map((date) => (
                        <option key={date} value={date}>{formatDate(date)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      <MapPin className="w-4 h-4 inline mr-1 text-red-600 dark:text-red-400" />
                      Location
                    </label>
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Locations</option>
                      {availableFilters.locations?.map((location) => (
                        <option key={location} value={location}>{location}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reports Table */}
        {reportsData?.reports && reportsData.reports.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden mb-8 transition-colors duration-300">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={input => {
                            if (input) {
                              input.indeterminate = isIndeterminate;
                            }
                          }}
                          onChange={toggleSelectAll}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                        />
                        <span className="ml-2">Select All</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Report Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Patient ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Test Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Report Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {reportsData.reports.map((report) => (
                    <tr 
                      key={report.id} 
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedReports.has(report.id)}
                          onChange={() => toggleSelectReport(report.id)}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {report.name || `Report for ${report.patient_id}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {report.patient_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(report.test_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(report.report_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {report.location || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => viewPDF(report.pdf_url)}
                            disabled={!report.pdf_url}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => downloadPDF(report.pdf_url, report.name || `ECG_Report_${report.patient_id}`)}
                            disabled={!report.pdf_url}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          !loading && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
                No Reports Found
              </h3>
              <p className="text-gray-400 dark:text-gray-500 mb-4">
                {searchTerm || selectedTestDate || selectedReportDate || selectedLocation 
                  ? "No reports match your current filters. Try adjusting your search criteria."
                  : "There are no ECG reports available for your account."}
              </p>
              {(searchTerm || selectedTestDate || selectedReportDate || selectedLocation) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 mb-3"
                >
                  Clear Filters
                </button>
              )}
              <button
                onClick={refreshData}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          )
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 transition-colors duration-300">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {pagination.current_page} of {pagination.total_pages}
                {pagination.total_items && ` (${pagination.total_items} total items)`}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_previous}
                  className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    const pageNum = Math.max(1, pagination.current_page - 2) + i;
                    if (pageNum <= pagination.total_pages) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 rounded-lg transition-colors duration-200 ${
                            pageNum === pagination.current_page
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next}
                  className="flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ECGReportsPage;